import { useEffect, useRef, createContext, useContext, useState, ReactNode, useCallback } from 'react';
import Lenis from 'lenis';

interface SmoothScrollContextType {
    lenis: Lenis | null;
    scrollProgress: number;
    scrollToTop: () => void;
}

const SmoothScrollContext = createContext<SmoothScrollContextType>({
    lenis: null,
    scrollProgress: 0,
    scrollToTop: () => {},
});

export const useSmoothScroll = () => useContext(SmoothScrollContext);

interface SmoothScrollProviderProps {
    children: ReactNode;
}

/**
 * SmoothScrollProvider
 * 
 * Wraps the app with Lenis smooth scrolling for that premium "weighted" feel.
 * Also tracks global scroll progress for animations.
 */
export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
    const lenisRef = useRef<Lenis | null>(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    // Scroll to top function using Lenis
    const scrollToTop = useCallback(() => {
        if (lenisRef.current) {
            lenisRef.current.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }, []);

    useEffect(() => {
        // Initialize Lenis with premium settings
        const lenis = new Lenis({
            duration: 1.2, // Slow, luxurious scroll
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential ease out
            orientation: 'vertical',
            gestureOrientation: 'vertical',
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
        });

        lenisRef.current = lenis;

        // RAF loop for Lenis
        function raf(time: number) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);

        // Track scroll progress
        lenis.on('scroll', ({ progress }: { progress: number }) => {
            setScrollProgress(progress);
        });

        return () => {
            lenis.destroy();
        };
    }, []);

    return (
        <SmoothScrollContext.Provider value={{ lenis: lenisRef.current, scrollProgress, scrollToTop }}>
            {children}
        </SmoothScrollContext.Provider>
    );
}

export default SmoothScrollProvider;
